import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Play, Zap, Target, Trophy, Beaker, FlaskConical, Search, Lock, Sparkles } from 'lucide-react';
import type { ModeMetadata } from '../modes';
import { CardSurface } from './shared/CardSurface';
interface LabStationProps {
  mode: ModeMetadata;
  personalNote?: string;
  masteryPercent?: number;
  statLabel?: string;
  personalRecord?: string;
  /** When true, renders a subtle pulse-ring indicating new content (F-UI-4). */
  newlyUnlocked?: boolean;
  onClick: () => void;
}
const iconMap = {
  Zap,
  Target,
  Trophy,
  Beaker,
  FlaskConical,
  Search
};
const colorMap: Record<string, {
  bg: string;
  text: string;
  border: string;
  gradient: string;
  shadow: string;
  ring: string;
}> = {
  Zap: {
    bg: 'bg-amber-100',
    text: 'text-amber-600',
    border: 'border-amber-200',
    gradient: 'from-amber-400 to-orange-500',
    shadow: 'shadow-amber-200',
    ring: 'ring-amber-300'
  },
  Target: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    border: 'border-indigo-200',
    gradient: 'from-indigo-400 to-purple-500',
    shadow: 'shadow-indigo-200',
    ring: 'ring-indigo-300'
  },
  Trophy: {
    bg: 'bg-violet-100',
    text: 'text-violet-600',
    border: 'border-violet-200',
    gradient: 'from-violet-400 to-fuchsia-500',
    shadow: 'shadow-violet-200',
    ring: 'ring-violet-300'
  },
  Beaker: {
    bg: 'bg-teal-100',
    text: 'text-teal-600',
    border: 'border-teal-200',
    gradient: 'from-teal-400 to-emerald-500',
    shadow: 'shadow-teal-200',
    ring: 'ring-teal-300'
  },
  FlaskConical: {
    bg: 'bg-rose-100',
    text: 'text-rose-600',
    border: 'border-rose-200',
    gradient: 'from-rose-400 to-pink-500',
    shadow: 'shadow-rose-200',
    ring: 'ring-rose-300'
  },
  Search: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    gradient: 'from-emerald-400 to-teal-500',
    shadow: 'shadow-emerald-200',
    ring: 'ring-emerald-300'
  }
};
const ctaTextMap: Record<string, string> = {
  fluency: 'Train snelheid',
  sprint: 'Start sprint',
  challenge: 'Accepteer uitdaging',
  free: 'Kies onderwerp'
};
/**
 * LabStation — Interactive card for a single practice mode.
 *
 * Audit (Learning Lab v1.1):
 * - F-UI-3: Djay-bubble is verwijderd uit de card — Djay blijft in de page-hero
 *   en MissionBriefing zodat de mascotte zijn zeldzaamheid houdt.
 * - F-UI-4: Subtle pulse-ring wanneer `newlyUnlocked` actief is.
 * - F-FEEL-6: Disabled-state krijgt subtle tremor + slot-icoon ipv stilte.
 * - F-UI-5: Flattened DOM structure using CardSurface primitive.
 *
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function LabStation({
  mode,
  personalNote,
  masteryPercent,
  statLabel,
  personalRecord,
  newlyUnlocked = false,
  onClick
}: LabStationProps) {
  const Icon = iconMap[mode.icon as keyof typeof iconMap] || Beaker;
  const colors = colorMap[mode.icon] || colorMap.Beaker;
  const isDisabled = !mode.isEnabled;
  const ctaText = ctaTextMap[mode.id] || 'Start';
  return <CardSurface isDisabled={isDisabled} onClick={onClick} className={`h-full w-full flex flex-col p-6 rounded-[2rem] transition-all overflow-hidden group ${isDisabled ? 'bg-slate-100 opacity-70 cursor-not-allowed text-slate-500' : `bg-gradient-to-br ${colors.gradient} cursor-pointer hover:shadow-xl shadow-lg text-white`} ${newlyUnlocked && !isDisabled ? `ring-4 ring-offset-2 ring-offset-white ${colors.ring}` : ''}`} data-id="element-2682">
      {/* F-UI-4: pulse-ring overlay for newly-unlocked content */}
      {newlyUnlocked && !isDisabled && <motion.div aria-hidden className={`absolute inset-0 rounded-[2rem] ring-4 ${colors.ring} pointer-events-none`} animate={{
      opacity: [0.6, 0, 0.6],
      scale: [1, 1.04, 1]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-2683" />}

      {/* F-UI-4: "Nieuw!" badge top-right when newly unlocked */}
      {newlyUnlocked && !isDisabled && <div className="absolute top-3 right-3 z-20 flex items-center gap-1 px-2 py-1 rounded-full bg-white text-[10px] font-black uppercase tracking-wider shadow-md" data-id="element-2684">
          <Sparkles className={`w-3 h-3 ${colors.text}`} data-id="element-2685" />
          <span className={colors.text} data-id="element-2686">Nieuw!</span>
        </div>}

      {/* Top Row: Icon & Time */}
      <div className="flex items-start justify-between mb-4 relative z-10 w-full" data-id="element-2687">
        <motion.div whileHover={!isDisabled ? {
        rotate: [0, -10, 10, -10, 0]
      } : {}} transition={{
        duration: 0.5
      }} className={`w-14 h-14 rounded-2xl flex items-center justify-center backdrop-blur-sm relative
            ${isDisabled ? 'bg-slate-200' : 'bg-white/20 shadow-inner'}`} data-id="element-2688">
          <Icon className={`w-7 h-7 ${isDisabled ? 'text-slate-400' : 'text-white'}`} strokeWidth={2.5} data-id="element-2689" />
          {isDisabled && <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center shadow-sm" data-id="element-2690">
              <Lock className="w-3 h-3 text-slate-600" strokeWidth={3} data-id="element-2691" />
            </div>}
        </motion.div>

        <div className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur-sm
          ${isDisabled ? 'bg-slate-200 text-slate-500' : 'bg-white/20 text-white'}`} data-id="element-2692">
          <Clock className="w-4 h-4" data-id="element-2693" />
          <span data-id="element-2694">{mode.estimatedMinutes}m</span>
        </div>
      </div>

      {/* Title & Subtitle */}
      <div className="mb-4 flex-1 relative z-10 w-full" data-id="element-2695">
        <h3 className="text-2xl font-black tracking-tight leading-tight mb-1" data-id="element-2696">
          {mode.title}
        </h3>
        <p className={`text-sm font-medium line-clamp-3 ${isDisabled ? 'text-slate-400' : 'text-white/90'}`} data-id="element-2697">
          {mode.description}
        </p>
        {personalRecord && !isDisabled && <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/10 text-xs font-bold text-white/90" data-id="element-2698">
            <Trophy className="w-3.5 h-3.5" data-id="element-2699" />
            {personalRecord}
          </div>}
      </div>

      {/* Progress Bar */}
      {masteryPercent !== undefined && !isDisabled && <div className="mb-4 relative z-10 w-full" data-id="element-2700">
          <div className="flex justify-between items-center mb-1.5" data-id="element-2701">
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/80" data-id="element-2702">
              Voortgang
            </span>
            <span className="text-[10px] font-bold text-white" data-id="element-2703">
              {Math.round(masteryPercent)}%
            </span>
          </div>
          <div className="h-1.5 w-full bg-black/10 rounded-full overflow-hidden" data-id="element-2704">
            <motion.div className="h-full bg-white rounded-full" initial={{
          width: 0
        }} animate={{
          width: `${Math.max(0, Math.min(100, masteryPercent))}%`
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} data-id="element-2705" />
          </div>
        </div>}

      {/* Action Area */}
      <div className={`pt-4 flex items-center justify-between border-t relative z-10 w-full ${isDisabled ? 'border-slate-200' : 'border-white/20'}`} data-id="element-2706">
        <div className={`text-xs font-bold ${isDisabled ? 'text-slate-400' : 'text-white/90'} line-clamp-1 pr-2`} data-id="element-2707">
          {statLabel || personalNote || mode.tags[0]}
        </div>

        {!isDisabled ? <div className={`px-4 py-2 rounded-xl bg-white text-sm font-black flex items-center gap-2 transition-transform group-hover:scale-105 shadow-sm ${colors.text}`} data-id="element-2708">
            {ctaText}
            <Play className="w-4 h-4" fill="currentColor" data-id="element-2709" />
          </div> : <span className="text-xs font-black bg-slate-200 text-slate-400 px-4 py-2 rounded-xl inline-flex items-center gap-1.5" data-id="element-2710">
            <Lock className="w-3.5 h-3.5" data-id="element-2711" />
            Binnenkort
          </span>}
      </div>
    </CardSurface>;
}